/**
 * Proposals API
 * =============
 * API functions for proposal management, decision engine, and external research
 */

import apiClient from './client';

/**
 * Get decision engine output for a lead
 */
export const getDecisionEngineOutput = async (leadId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/decision-engine`);
  return response.data;
};

/**
 * Get decision engine processing status
 */
export const getDecisionEngineStatus = async (leadId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/decision-engine/status`);
  return response.data;
};

/**
 * Proceed from decision engine stage
 */
export const proceedFromDecisionEngine = async (leadId, decisionEngineId, data = {}) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/decision-engine/${decisionEngineId}/proceed`, data);
  return response.data;
};

/**
 * Decline from decision engine stage
 */
export const declineFromDecisionEngine = async (leadId, decisionEngineId, data = {}) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/decision-engine/${decisionEngineId}/decline`, data);
  return response.data;
};

/**
 * Manually trigger decision engine analysis
 */
export const triggerDecisionEngine = async (leadId) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/decision-engine/trigger`);
  return response.data;
};

/**
 * Get external research output for a lead
 */
export const getExternalResearch = async (leadId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/external-research`);
  return response.data;
};

/**
 * Get external research processing status
 */
export const getExternalResearchStatus = async (leadId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/external-research/status`);
  return response.data;
};

/**
 * Proceed from external research stage
 */
export const proceedFromExternalResearch = async (leadId, researchId, data) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/external-research/${researchId}/proceed`, data);
  return response.data;
};

/**
 * Decline from external research stage
 */
export const declineFromExternalResearch = async (leadId, researchId, data = {}) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/external-research/${researchId}/decline`, data);
  return response.data;
};

/**
 * Get combined review status (DE + ER)
 */
export const getProposalReviewStatus = async (leadId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/proposal-review/status`);
  return response.data;
};

/**
 * Combined proceed: approve DE + ER and trigger proposal generation
 */
export const proceedFromProposalReview = async (leadId, data) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposal-review/proceed`, data);
  return response.data;
};

/**
 * Combined decline: decline the pipeline
 */
export const declineFromProposalReview = async (leadId, data = {}) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposal-review/decline`, data);
  return response.data;
};

/**
 * Schedule follow-up: update proposal status to schedule_follow_up
 *
 * @param {string} leadId - Lead UUID
 * @param {Object} data - Follow-up data
 * @param {string} [data.comment] - Follow-up comment/reason
 */
export const scheduleFollowupFromProposalReview = async (leadId, data = {}) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposal-review/schedule-followup`, data);
  return response.data;
};

/**
 * Get count of proposals pending approval (for sidebar badge)
 */
export const getPendingProposalCount = async () => {
  const response = await apiClient.get('/api/v1/proposals/pending-count');
  return response.data;
};

/**
 * Get all proposals with filtering and pagination
 */
export const getAllProposals = async (params = {}) => {
  const response = await apiClient.get('/api/v1/proposals', { params });
  return response.data;
};

/**
 * Get all proposals for a specific lead
 */
export const getLeadProposals = async (leadId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/proposals`);
  return response.data;
};

/**
 * Get a specific proposal by ID
 */
export const getProposal = async (leadId, proposalId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/proposals/${proposalId}`);
  return response.data;
};

/**
 * Get proposal processing status
 *
 * Used for auto-polling during AI proposal generation.
 * Returns whether processing is in progress, stuck, completed, or failed.
 *
 * @param {string} leadId - Lead UUID
 * @param {string} proposalId - Proposal UUID
 * @returns {Promise<Object>} Processing status with fields:
 *   - is_processing: boolean - Whether AI is currently processing
 *   - status: string - "processing", "completed", "failed", "stuck", or "not_started"
 *   - should_poll: boolean - Whether frontend should continue polling
 *   - poll_interval_ms: number - Recommended polling interval in milliseconds
 *   - message: string - Human-readable status message
 *   - last_error: string|null - Error message if failed
 *   - processing_started_at: string|null - ISO timestamp when processing started
 * @throws {Error} 404 - Lead or proposal not found
 */
export const getProposalProcessingStatus = async (leadId, proposalId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/proposals/${proposalId}/processing-status`);
  return response.data;
};

/**
 * Get worker outputs for a proposal
 *
 * Returns the output from all 4 AI intelligence workers:
 * - Origination Intelligence
 * - Technology Intelligence
 * - Regulatory & Structuring Intelligence
 * - Distribution Intelligence
 */
export const getWorkerOutputs = async (leadId, proposalId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/proposals/${proposalId}/workers`);
  return response.data;
};

/**
 * Regenerate a proposal
 *
 * Triggers AI to regenerate the proposal from existing questionnaire responses.
 */
export const regenerateProposal = async (leadId, proposalId) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/regenerate`);
  return response.data;
};

/**
 * Submit a review for a proposal
 *
 * @param {string} leadId - Lead UUID
 * @param {string} proposalId - Proposal UUID
 * @param {Object} reviewData - Review data
 * @param {string} reviewData.review_type - Type of review (qa_review, client_review, etc.)
 * @param {string} reviewData.status - Review status (approved, rejected, needs_revision)
 * @param {string} [reviewData.comments] - Review comments
 * @param {Object} [reviewData.feedback] - Structured feedback
 */
export const submitReview = async (leadId, proposalId, reviewData) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/review`, reviewData);
  return response.data;
};

/**
 * Get all reviews for a proposal
 */
export const getReviews = async (leadId, proposalId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/proposals/${proposalId}/reviews`);
  return response.data;
};

/**
 * Update proposal status
 *
 * @param {string} leadId - Lead UUID
 * @param {string} proposalId - Proposal UUID
 * @param {Object} statusData - Status update data
 * @param {string} statusData.status - New status (draft, pending_qa, approved, sent_to_client, accepted, rejected, expired)
 * @param {string} [statusData.notes] - Optional notes about the status change
 */
export const updateProposalStatus = async (leadId, proposalId, statusData) => {
  const response = await apiClient.patch(`/api/v1/leads/${leadId}/proposals/${proposalId}/status`, statusData);
  return response.data;
};

/**
 * Add a comment to a specific proposal section
 */
export const addSectionComment = async (leadId, proposalId, data) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/section-comments`, data);
  return response.data;
};

/**
 * Delete a section comment
 */
export const deleteSectionComment = async (leadId, proposalId, commentId) => {
  const response = await apiClient.delete(`/api/v1/leads/${leadId}/proposals/${proposalId}/section-comments/${commentId}`);
  return response.data;
};

/**
 * Dismiss all open section comments (cancel revision)
 */
export const dismissAllSectionComments = async (leadId, proposalId) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/section-comments/dismiss-all`);
  return response.data;
};

/**
 * Get section comments for a proposal
 */
export const getSectionComments = async (leadId, proposalId, sectionNumber = null) => {
  const params = sectionNumber ? { section_number: sectionNumber } : {};
  const response = await apiClient.get(`/api/v1/leads/${leadId}/proposals/${proposalId}/section-comments`, { params });
  return response.data;
};

/**
 * Update a section's content (manual edit)
 */
export const updateSectionContent = async (leadId, proposalId, sectionNumber, contentData) => {
  const response = await apiClient.patch(`/api/v1/leads/${leadId}/proposals/${proposalId}/sections/${sectionNumber}`, contentData);
  return response.data;
};

/**
 * Apply the open section comments to this proposal in place (no new version).
 *
 * The body carries no feedback text — the server reads the stored comments.
 * `section_numbers` narrows which comments are acted on (null = all of them);
 * it does not limit which sections may change, since a factual correction
 * propagates to every section stating the old value.
 */
export const reviseProposal = async (leadId, proposalId, data = {}) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/revise`, data);
  return response.data;
};

/**
 * Submit a proposal for admin review with optional comment
 */
export const submitForReview = async (leadId, proposalId, data = {}) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/submit-for-review`, data);
  return response.data;
};

/**
 * Mark an approved proposal as sent to client
 */
export const sendToClient = async (leadId, proposalId, data = {}) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/send-to-client`, data);
  return response.data;
};

/**
 * Get email preview content for send-to-client
 */
export const getSendToClientPreview = async (leadId, proposalId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/proposals/${proposalId}/send-to-client/preview`);
  return response.data;
};

/**
 * Send a proposal back to BD team for updates (admin only)
 */
export const sendBack = async (leadId, proposalId, data) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/send-back`, data);
  return response.data;
};

/**
 * Cancel a review request and return proposal to draft
 */
export const cancelReview = async (leadId, proposalId) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/cancel-review`);
  return response.data;
};

/**
 * Activate a specific proposal version
 *
 * Sets the target proposal as the active version for its lead,
 * deactivating all other versions.
 *
 * @param {string} leadId - Lead UUID
 * @param {string} proposalId - Proposal UUID to activate
 */
export const activateProposal = async (leadId, proposalId) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/activate`);
  return response.data;
};

/**
 * Force generate a proposal despite a low viability rating
 *
 * Bypasses the rating threshold gate and proceeds to Orchestrator assembly.
 *
 * @param {string} leadId - Lead UUID
 * @param {string} proposalId - Proposal UUID
 */
export const forceGenerateProposal = async (leadId, proposalId) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/force-generate`);
  return response.data;
};

/**
 * Generate narration scripts for avatar presentation
 */
export const generateNarrations = async (leadId, proposalId) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/proposals/${proposalId}/narrations/generate`);
  return response.data;
};

/**
 * Save manually edited narrations
 */
export const updateNarrations = async (leadId, proposalId, narrations) => {
  const response = await apiClient.put(`/api/v1/leads/${leadId}/proposals/${proposalId}/narrations`, { narrations });
  return response.data;
};

/**
 * List all avatar configurations
 */
export const getAvatarConfigs = async () => {
  const response = await apiClient.get('/api/v1/avatar-configs');
  return response.data;
};

/**
 * Create a new avatar configuration
 */
export const createAvatarConfig = async (data) => {
  const response = await apiClient.post('/api/v1/avatar-configs', data);
  return response.data;
};

/**
 * Update an avatar configuration
 */
export const updateAvatarConfig = async (configId, data) => {
  const response = await apiClient.patch(`/api/v1/avatar-configs/${configId}`, data);
  return response.data;
};

/**
 * Delete an avatar configuration
 */
export const deleteAvatarConfig = async (configId) => {
  const response = await apiClient.delete(`/api/v1/avatar-configs/${configId}`);
  return response.data;
};

/**
 * Set an avatar configuration as active
 */
export const activateAvatarConfig = async (configId) => {
  const response = await apiClient.post(`/api/v1/avatar-configs/${configId}/activate`);
  return response.data;
};

const proposalAPI = {
  getDecisionEngineOutput,
  getDecisionEngineStatus,
  proceedFromDecisionEngine,
  declineFromDecisionEngine,
  triggerDecisionEngine,
  getExternalResearch,
  getExternalResearchStatus,
  proceedFromExternalResearch,
  declineFromExternalResearch,
  getProposalReviewStatus,
  proceedFromProposalReview,
  declineFromProposalReview,
  scheduleFollowupFromProposalReview,
  getAllProposals,
  getLeadProposals,
  getProposal,
  getProposalProcessingStatus,
  getWorkerOutputs,
  regenerateProposal,
  submitReview,
  getReviews,
  updateProposalStatus,
  addSectionComment,
  deleteSectionComment,
  getSectionComments,
  updateSectionContent,
  reviseProposal,
  submitForReview,
  cancelReview,
  activateProposal,
  dismissAllSectionComments,
  forceGenerateProposal,
  getSendToClientPreview,
  generateNarrations,
  updateNarrations,
  getAvatarConfigs,
  createAvatarConfig,
  updateAvatarConfig,
  deleteAvatarConfig,
  activateAvatarConfig,
};

export default proposalAPI;
